import { useState, useRef, useEffect } from 'react';
import QRCode from 'qrcode';
import { Download, Copy, Home, LogOut } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import './QRCodePage.css';

const QRCodePage = ({ personData, onLogout }) => {
  const { person } = useParams();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const canvasRef = useRef(null);
  const [qrDataUrl, setQrDataUrl] = useState('');

  // Determine which person and their link
  const personKey = person ? person.toLowerCase() : 'fidlogic';
  const currentPerson = personData[personKey] || personData['fidlogic'];
  
  // Generate the link that will be encoded in QR code
  const baseURL = window.location.origin;
  const contactLink = person ? `${baseURL}/${person}` : `${baseURL}/fidlogic`;
  const isCompany = personKey.includes('fidlogic');
  const qrTitle = isCompany ? 'FidLogic QR Code' : `QR Code for ${currentPerson.name}`;

  // Generate QR code with logo overlay
  useEffect(() => {
    if (canvasRef.current) {
      QRCode.toCanvas(canvasRef.current, contactLink, {
        errorCorrectionLevel: 'H',
        type: 'image/png',
        quality: 0.95,
        margin: 2,
        width: 300,
        color: {
          dark: '#000000',
          light: '#FFFFFF',
        },
      }, (error) => {
        if (error) {
          console.error('QR Code generation error:', error);
          return;
        }

        // Add logo overlay to QR code
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const logoImg = new Image();
        
        // Load the FidLogic logo
        logoImg.onload = () => {
          // Create a white background square for the logo (about 1/3 of QR size)
          const logoSize = 80;
          const padding = 4;
          const x = (canvas.width - logoSize) / 2;
          const y = (canvas.height - logoSize) / 2;
          
          // Draw white background square
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(x - padding, y - padding, logoSize + padding * 2, logoSize + padding * 2);
          
          // Draw logo on top
          ctx.drawImage(logoImg, x, y, logoSize, logoSize);
          
          // Update data URL with logo overlay
          setQrDataUrl(canvas.toDataURL('image/png'));
        };
        
        logoImg.onerror = () => {
          console.warn('Could not load logo image');
          setQrDataUrl(canvas.toDataURL('image/png'));
        };
        
        // Load logo from public assets
        logoImg.src = '/Logo - only - no bg - 336X336.png';
      });
    }
  }, [contactLink]);

  // Download QR code
  const downloadQRCode = () => {
    if (qrDataUrl) {
      const link = document.createElement('a');
      link.href = qrDataUrl;
      link.download = `${personKey}-qrcode.png`;
      link.click();
    }
  };

  // Copy link to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(contactLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Navigate to contact QR and to top of page
  const navigateToContactQR = (contactPerson) => {
    navigate(`/qr/${contactPerson}`);
    window.scrollTo({top: 0, behavior: 'smooth'}); // Scroll to the top of the page
  };

  return (
    <div className="qr-page">
      <div className="qr-container">
        {/* Header */}
        <div className="qr-header">
          <button className="back-btn" onClick={() => navigate('/')}>
            <Home size={20} />
            <span>Back Home</span>
          </button>
          <h1>{qrTitle}</h1>
          <button className="logout-btn" onClick={onLogout} title="Logout">
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>

        {/* Main QR Code Section */}
        <div className="qr-content">
          {/* QR Code Display */}
          <div className="qr-display">
            <div className="qr-code-wrapper">
              <canvas
                ref={canvasRef}
                id="qr-code"
                style={{
                  borderRadius: '10px',
                  display: 'block',
                }}
              />
            </div>
            <p className="qr-description">
              Scan this QR code to view {person ? `${currentPerson.name}'s` : 'the FidLogic'} contact card
            </p>
          </div>

          {/* Info Section */}
          <div className="qr-info">
            <div className="info-item">
              <h3>🔗 Contact Link</h3>
              <div className="link-display">
                <code>{contactLink}</code>
                <button 
                  className={`copy-btn ${copied ? 'copied' : ''}`}
                  onClick={copyToClipboard}
                  title="Copy to clipboard"
                >
                  <Copy size={18} />
                  <span>{copied ? 'Copied!' : 'Copy'}</span>
                </button>
              </div>
            </div>

            {isCompany && (
              <div className="info-item">
                <h3>🏢 Company Details</h3>
                <div className="person-details">
                  <p><strong>Name:</strong> {currentPerson.name}</p>
                  <p><strong>Description:</strong> {currentPerson.designation}</p>
                  <p><strong>Email:</strong> {currentPerson.email}</p>
                  <p><strong>Phone:</strong> {currentPerson.phone}</p>
                </div>
              </div>
            )}

            {!isCompany && (
              <div className="info-item">
                <h3>👤 Person Details</h3>
                <div className="person-details">
                  <p><strong>Name:</strong> {currentPerson.name}</p>
                  <p><strong>Title:</strong> {currentPerson.designation}</p>
                  <p><strong>Company:</strong> {currentPerson.company}</p>
                  <p><strong>Email:</strong> {currentPerson.email}</p>
                  <p><strong>Phone:</strong> {currentPerson.phone}</p>
                </div>
              </div>
            )}

            <div className="info-item">
              <h3>📥 Download</h3>
              <button className="download-btn" onClick={downloadQRCode} disabled={!qrDataUrl}>
                <Download size={20} />
                <span>Download QR Code</span>
              </button>
            </div>
          </div>
        </div>

        {/* Usage Guide */}
        <div className="qr-guide">
          <h2>How to Use QR Codes</h2>
          <div className="guide-content">
            <div className="guide-item">
              <h4>1. Generate QR Code</h4>
              <p>Visit these URLs to generate QR codes:</p>
              <ul>
                <li><code>/qr</code> → Main FidLogic QR code</li>
                <li><code>/qr/[name]</code> → Any team member's QR code</li>
              </ul>
            </div>

            <div className="guide-item">
              <h4>2. Download & Share</h4>
              <p>
                Click "Download QR Code" to save the image, then:
              </p>
              <ul>
                <li>Print it on business cards</li>
                <li>Add it to your email signature</li>
                <li>Post it on social media</li>
                <li>Include it in presentations</li>
              </ul>
            </div>

            <div className="guide-item">
              <h4>3. Scan & Connect</h4>
              <p>
                Anyone can scan the QR code with their phone camera to:
              </p>
              <ul>
                <li>View the contact card instantly</li>
                <li>Access all company services</li>
                <li>Find social media links</li>
                <li>Get contact information</li>
              </ul>
            </div>
          </div>
        </div>

        {/* All Team Members QR Codes */}
        <div className="qr-team-section">
          <h2>Team Members QR Codes</h2>
          <div className="team-qr-grid">
            {Object.entries(personData).map(([key, personInfo]) => (
              <button
                key={key}
                className="team-qr-item"
                onClick={() => navigateToContactQR(key)} // Call the custom function to navigate to the contact QR code 
              >
                <div className="team-qr-preview">
                  <QRCodeCanvas
                    value={`${baseURL}/${key}`}
                    size={150}
                  />
                </div>
                <h4>{personInfo.name}</h4>
                <p>{personInfo.designation}</p>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Mini QR Code component for the grid
const QRCodeCanvas = ({ value, size }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (canvasRef.current) {
      QRCode.toCanvas(canvasRef.current, value, {
        errorCorrectionLevel: 'H',
        type: 'image/png',
        quality: 0.95,
        margin: 1,
        width: size,
        color: {
          dark: '#000000',
          light: '#FFFFFF',
        },
      }, (error) => {
        if (!error) {
          // Add logo overlay to mini QR codes too
          const canvas = canvasRef.current;
          const ctx = canvas.getContext('2d');
          const logoImg = new Image();
          
          logoImg.onload = () => {
            const logoSize = size / 4; // Smaller logo for mini QR
            const padding = 2;
            const x = (canvas.width - logoSize) / 2;
            const y = (canvas.height - logoSize) / 2;
            
            // Draw white background
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(x - padding, y - padding, logoSize + padding * 2, logoSize + padding * 2);
            
            // Draw logo
            ctx.drawImage(logoImg, x, y, logoSize, logoSize);
          };
          
          logoImg.src = '/Logo - only - no bg - 336X336.png';
        }
      });
    }
  }, [value, size]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        borderRadius: '8px',
        display: 'block',
        width: '100%',
        height: 'auto',
      }}
    />
  );
};

export default QRCodePage;
